import React, { useEffect, useMemo, useState } from "react";

import { MaintenanceDto, MdmObjectDto, useGetObjectsByProjectQuery } from "gql/generated/types";
import L, { LatLngBounds, LatLngTuple } from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { useMap, ZoomControl } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { T } from "services/lang/LanguageService";
import useProjectContext from "../../hooks/useProjectContext";
import { Status, StatusInfo } from "../../utils/types";
import { apolloErrorToaster, renderTag } from "../../utils/utils";
import MapWrapper from "../MapWrapper";
import { defaultIcon, ObjectMarker } from "../ObjectMapContent";
import { maintenanceStatuses } from "../../utils/const";

const optionStatuses = ["DELAYED", "EXTENDED", "IN_PROGRESS", "PLANNED", "COORDINATED"];
const statuses: StatusInfo[] = [...maintenanceStatuses]
  .filter(s => optionStatuses.includes(s.value))
  .reverse();

const defaultIconProps: L.IconOptions = {
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconAnchor: [12, 40],
  popupAnchor: [0, -40],
};
const delayedMarker = L.icon({ ...defaultIconProps, className: "delayed-icon" });
const extendedMarker = L.icon({ ...defaultIconProps, className: "extended-icon" });
const inProgressMarker = L.icon({ ...defaultIconProps, className: "in-progress-icon" });
const coordinatedMarker = L.icon({ ...defaultIconProps, className: "coordinated-icon" });

const MaintenanceMap: React.FC<{ data: MaintenanceDto[] }> = ({ data }) => {
  const [selectedStatus, setSelectedStatus] = useState<Status[]>(() => statuses.map(s => s.value));
  const { projectId } = useProjectContext();
  const { data: objects, loading } = useGetObjectsByProjectQuery({
    variables: {
      id: projectId!,
    },
    skip: !projectId,
    onError: apolloErrorToaster,
  });

  const getIcon = (status: Status) => {
    switch (status) {
      case "DELAYED":
        return delayedMarker;
      case "EXTENDED":
        return extendedMarker;
      case "IN_PROGRESS":
        return inProgressMarker;
      case "COORDINATED":
        return coordinatedMarker;
      default:
        return defaultIcon;
    }
  };

  const filteredData = useMemo(
    () => data.filter(m => selectedStatus.includes(m.status)),
    [data, selectedStatus]
  );
  const maintenanceObjects = filteredData.flatMap(m =>
    m.objectIds?.map(o => ({
      ...objects?.mdmObjectsByProjectId.find(obj => obj.id === o),
      icon: getIcon(m.status),
    }))
  ) as (MdmObjectDto & { icon: L.Icon<L.IconOptions> })[];

  return (
    <div className="relative">
      <MultiSelect
        value={selectedStatus}
        onChange={e => setSelectedStatus(e.value)}
        options={statuses}
        optionLabel="translationKey"
        optionValue="value"
        className="absolute top-0 left-0 m-2"
        style={{ zIndex: 900 }}
        itemTemplate={opt => renderTag({ status: opt.value })}
        maxSelectedLabels={0}
        selectedItemsLabel={`${T("label.selected-options")}: {0}`}
        placeholder={T("word.select-one")}
      />
      <MapWrapper>
        {loading && <ProgressSpinner className="mt-8 w-full" style={{ zIndex: 400 }} />}
        <ObjectMapContent objects={maintenanceObjects} />
      </MapWrapper>
    </div>
  );
};

export default MaintenanceMap;

const ObjectMapContent: React.FC<{
  objects: (MdmObjectDto & { icon: L.Icon<L.IconOptions> })[];
}> = ({ objects }) => {
  const map = useMap();

  const markers = useMemo(
    () =>
      objects?.reduce((tuples, object) => {
        if (!object?.location?.latitude || !object?.location?.longitude) return tuples;
        tuples.push([object?.location?.latitude, object?.location?.longitude]);
        return tuples;
      }, [] as LatLngTuple[]),
    [objects]
  );

  useEffect(() => {
    if (markers.length === 0)
      map.fitBounds([
        [54.2, 23.5],
        [56.2, 24.5],
      ]);
    if (markers.length === 1) map.setView(markers[0], 16);
    if (markers.length > 1) {
      const bounds = new LatLngBounds(markers);
      const northEast = Object.values(bounds.getNorthEast()) as LatLngTuple;
      const southWest = Object.values(bounds.getSouthWest()) as LatLngTuple;
      map.fitBounds([northEast, southWest]);
    }
  }, [map, markers]);

  const mapMarkers = objects?.map((object, index) => (
    <ObjectMarker
      // eslint-disable-next-line react/no-array-index-key
      key={`${object!.id}-${index}`}
      object={object}
      markerIcon={object.icon}
    />
  ));

  return (
    <div>
      <ZoomControl position="bottomright" />
      <MarkerClusterGroup maxClusterRadius={28}>{mapMarkers}</MarkerClusterGroup>
    </div>
  );
};
